import { storage } from "@resamare/functions";
import { Admin, Role } from "@resamare/models";
import { useQueryClient } from "@tanstack/react-query";
import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { apiClientScanette, initApiClient } from "../../../config";
import { getAdmin } from "../api/getAdmin";
import { LoginDTO, login } from "../api/login";

export enum AuthStatus {
    UNKNOWN = "unknown",
    AUTHENTICATED = "authenticated",
    GUEST = "guest",
}

interface AuthContextProps {
    admin: Admin | null | undefined;
    authStatus: AuthStatus;
    isLoading: boolean;
    authenticate: () => Promise<void>;
    login: (data: LoginDTO) => Promise<void>;
    logout: () => Promise<void>;
    role: Role;
    setAdmin: Dispatch<SetStateAction<Admin | null | undefined>>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthProvider({
    children,
    isScanette = false,
}: {
    children: ReactNode;
    isScanette?: boolean;
}) {
    const [admin, setAdmin] = useState<Admin | null | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    let authStatus: AuthStatus = AuthStatus.UNKNOWN;
    let role: Role = Role.GUEST;

    switch (admin) {
        case undefined:
            authStatus = AuthStatus.UNKNOWN;
            role = Role.GUEST;
            break;
        case null:
            authStatus = AuthStatus.GUEST;
            role = Role.GUEST;
            break;
        default:
            authStatus = AuthStatus.AUTHENTICATED;
            role = Role.ADMIN;
            break;
    }

    const authenticate = useCallback(async () => {
        const token = storage.getWithExpiry<string>("token");
        const apiUrl = storage.get<string>("apiUrl");

        const apiUrlScanette = storage.get<string>("apiUrlScanette");
        const storedAdmin = storage.get<string>("admin");

        if (!token || !apiUrl || (isScanette && !apiUrlScanette) || (isScanette && !storedAdmin)) {
            setAdmin(null);
            return;
        }

        initApiClient({ api: apiUrl, jwt: token });

        if (isScanette && apiUrlScanette) {
            apiClientScanette.defaults.baseURL = apiUrlScanette;
        }

        const adminOrNull = await getAdmin();

        setAdmin(adminOrNull);
    }, []);

    const loginFn = useCallback(async (data: LoginDTO) => {
        setIsLoading(true);
        try {
            const { token, apiUrl, apiUrlScanette } = await login(data);

            const TOKEN_TTL_20_HOURS = 20 * 60 * 60 * 1000;
            storage.setWithExpiry("token", token, TOKEN_TTL_20_HOURS);
            storage.set("apiUrl", apiUrl);

            initApiClient({ api: apiUrl, jwt: token });

            if (isScanette) {
                storage.set("apiUrlScanette", apiUrlScanette);
                apiClientScanette.defaults.baseURL = apiUrlScanette;
            }

            const adminOrNull = await getAdmin();
            if (adminOrNull) {
                storage.set("admin", {
                    admin_id: adminOrNull.admin_id,
                    url_web_service_client: apiClientScanette,
                });
            }

            setAdmin(adminOrNull);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logoutFn = useCallback(async () => {
        storage.remove("admin");
        storage.remove("apiUrl");
        storage.remove("apiUrlScanette");
        storage.remove("token");
        queryClient.removeQueries();
        setAdmin(null);
    }, []);

    const value = useMemo(
        () => ({
            admin,
            authStatus,
            isLoading,
            authenticate,
            login: loginFn,
            logout: logoutFn,
            role,
            setAdmin,
        }),
        [admin, authStatus, isLoading, authenticate, loginFn, logoutFn, role, setAdmin],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export function useAdmin() {
    const { admin } = useAuth();
    if (!admin) {
        throw new Error("User must be authenticated");
    }
    return { admin };
}
